// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useMemo, useRef, startTransition, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { formatDateYMD } from '../../../utils/dateRangeHelpers';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import {
  Filter,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Check,
  Calendar,
  Activity,
  Loader2,
  Edit2,
  X,
  AlertTriangle,
  History,
  Clock,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
// DatePicker importlarını buraya taşıyalım
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample ad groups data structure - Display kampanyalarına göre düzenlendi
const sampleAdGroups = {
  'display-1': [
    {
      id: 'ag1',
      name: 'Responsive Display Ads',
      status: 'Active',
      spend: 5200,
      // ...existing data...
    },
    {
      id: 'ag2',
      name: 'Banner Ads',
      status: 'Active',
      spend: 4800,
      // ...existing data...
    },
    // ...diğer ad gruplar
  ],
  // ...diğer kampanyalar
};

// Sample chart data
const chartData = {
  labels: ['1 Jan', '2 Jan', '3 Jan', '4 Jan', '5 Jan', '6 Jan', '7 Jan'],
  datasets: [
    {
      label: 'Spend',
      data: [300, 450, 320, 500, 350, 200, 400],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    },
    {
      label: 'Revenue',
      data: [500, 300, 400, 600, 450, 350, 550],
      borderColor: 'rgb(16, 185, 129)',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      type: 'linear',
      position: 'left',
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => {
          if (value >= 1000000) return `₺${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `₺${(value / 1000).toFixed(0)}K`;
          return `₺${value}`;
        }
      },
      title: {
        display: true,
        text: 'Cost & Revenue (₺)',
        color: '#6b7280',
        font: {
          size: 12,
          weight: '500'
        }
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        maxTicksLimit: 10
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 15,
        font: {
          size: 12
        }
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            if (label.includes('Cost') || label.includes('Revenue')) {
              label += `₺${context.parsed.y.toLocaleString('tr-TR', {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
            } else {
              label += context.parsed.y.toLocaleString('tr-TR');
            }
          }
          return label;
        }
      },
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleColor: '#f9fafb',
      bodyColor: '#f9fafb',
      borderColor: 'rgba(75, 85, 99, 0.2)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 12
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

export default function DisplayCampaigns({ forceDemandGen = false }) {
  const location = useLocation();
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');
  const isDemandGen = forceDemandGen || (location?.pathname || '').includes('/demand-gen');

  // Live status/budget overlay (fetched from Google Ads API)
  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);

  // Inline status/budget editing state
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editBudget, setEditBudget] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all');

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [keywordData, setKeywordData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Spend',
        data: [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Revenue',
        data: [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      }
    ]
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);
  
  // Display campaigns data state
  const [displayCampaignsData, setDisplayCampaignsData] = useState(null);
  // GDN placements state
  const [gdnPlacements, setGdnPlacements] = useState([]);
  const [gdnPlacementsRaw, setGdnPlacementsRaw] = useState([]);
  const [gdnPlacementTotals, setGdnPlacementTotals] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState(new Set());

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Custom date picker için state'leri ekleyelim
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  // Brand-specific dummy fallback for Display page
  const DUMMY_BRAND_ID = '9ad536a0-dbdf-4935-8730-f2c2029c6d52';
  const DEMO_BRAND_ID_SC = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';

  // Build rich dummy display campaigns + daily trend + raw rows
  const buildDummyDisplayData = (range) => {
    const campaigns = [];
    const rawData = [];
    const today = new Date();
    const days = 14;
    const groups = ['AW 2025 Display - Prospecting', 'Q4 Retargeting - Cart Abandoners', 'Brand & Awareness - Always On'];
    for (let i = 1; i <= 15; i++) {
      const group = groups[(i - 1) % groups.length];
      const impressions = 52000 + i * 4500;
      const clicks = 800 + i * 60;
      const cost = 700 + i * 85;
      const conversions = 5 + (i % 6);
      const revenue = 15000 + i * 4200;
      const sessions = 900 + i * 50;
      campaigns.push({
        campaignId: `D-C-${i}`,
        campaignName: `${group} ${i}`,
        status: 'ENABLED',
        budget: 5000 + i * 120,
        impressions,
        clicks,
        cost,
        conversions,
        revenue,
        sessions,
      });
      for (let d = 0; d < days; d++) {
        const date = new Date(today);
        date.setDate(today.getDate() - (days - 1 - d));
        const spend = Math.max(0, (cost / days) * (0.8 + (d % 5) * 0.05));
        const imp = Math.round(impressions / days * (0.85 + (d % 7) * 0.03));
        const clk = Math.round(clicks / days * (0.9 + (d % 4) * 0.04));
        const conv = d % 3 === 0 ? 1 : 0;
        const rev = Math.round(revenue / days * (0.9 + (d % 6) * 0.03));
        rawData.push({
          campaign_id: `D-C-${i}`,
          campaign_name: `${group} ${i}`,
          campaign_status: 'ENABLED',
          campaign_budget: 5000 + i * 120,
          date: date.toISOString().split('T')[0],
          cost: spend,
          impressions: imp,
          clicks: clk,
          conversions: conv,
          conversion_value: rev,
          ga4Revenue: Math.round(rev * 0.95),
          ga4Transactions: conv,
          sessions: Math.round(sessions / days * (0.9 + (d % 5) * 0.03)),
        });
      }
    }
    const dailyTrend = [];
    for (let d = 0; d < 14; d++) {
      const date = new Date(today);
      date.setDate(today.getDate() - (14 - 1 - d));
      const dateStr = date.toISOString().split('T')[0];
      const dayRows = rawData.filter(r => r.date === dateStr);
      const cost = dayRows.reduce((s, r) => s + Number(r.cost || 0), 0);
      const revenue = dayRows.reduce((s, r) => s + Number(r.conversion_value || 0), 0);
      dailyTrend.push({ date: dateStr, cost, revenue });
    }
    const totals = campaigns.reduce((acc, c) => {
      acc.impressions += Number(c.impressions || 0);
      acc.clicks += Number(c.clicks || 0);
      acc.cost += Number(c.cost || 0);
      acc.transactions += Number(c.conversions || 0);
      acc.revenue += Number(c.revenue || 0);
      acc.sessions += Number(c.sessions || 0);
      return acc;
    }, { impressions: 0, clicks: 0, cost: 0, transactions: 0, revenue: 0, sessions: 0 });
    // Aggregate GA4 from rawData for KPI cards
    const ga4Totals = rawData.reduce((acc, r) => {
      acc.ga4Revenue += Number(r.ga4Revenue || r.ga4_revenue || 0);
      acc.ga4Transactions += Number(r.ga4Transactions || r.ga4_transactions || 0);
      return acc;
    }, { ga4Revenue: 0, ga4Transactions: 0 });
    const totalMetrics = {
      impressions: totals.impressions,
      clicks: totals.clicks,
      cost: totals.cost,
      transactions: totals.transactions,
      revenue: totals.revenue,
      ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
      cpc: totals.clicks > 0 ? (totals.cost / totals.clicks) : 0,
      conversionRate: totals.clicks > 0 ? (totals.transactions / totals.clicks) * 100 : 0,
      roas: totals.cost > 0 ? (totals.revenue / totals.cost) : 0,
      sessions: totals.sessions,
      ga4Revenue: ga4Totals.ga4Revenue,
      ga4Transactions: ga4Totals.ga4Transactions,
      ga4Roas: totals.cost > 0 ? (ga4Totals.ga4Revenue / totals.cost) : 0
    };
    return { campaigns, dailyTrend, totalMetrics, rawData };
  };

  const buildDummyPlacements = () => {
    const domains = ['youtube.com', 'cnn.com', 'bbc.com', 'nytimes.com', 'hurriyet.com.tr', 'sozcu.com.tr', 'eksisozluk.com', 'mobile-app:com.game.racer', 'mobile-app:com.news.reader'];
    const types = ['PlacementType.WEBSITE', 'PlacementType.VIDEO', 'PlacementType.MOBILE_APP'];
    const rows = [];
    for (let i = 0; i < 60; i++) {
      const label = domains[i % domains.length];
      const type = types[i % types.length];
      rows.push({
        placement_group_url: label,
        placement_type: type,
        impressions: 3000 + (i % 9) * 500,
        clicks: 60 + (i % 7) * 12,
        cost: 90 + (i % 10) * 15,
      });
    }
    return rows;
  };

  // Ensure dummy campaigns/placements are present for the specific brand even if API didn't populate yet
  useEffect(() => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC) {
        const needCampaigns = !displayCampaignsData || !Array.isArray(displayCampaignsData.campaigns) || displayCampaignsData.campaigns.length === 0;
        if (needCampaigns) {
          const dummy = buildDummyDisplayData(dateRange);
          setDisplayCampaignsData(dummy);
          updateDisplayCampaignsChart(dummy.dailyTrend || []);
        }
        const needPlacements = !Array.isArray(gdnPlacementsRaw) || gdnPlacementsRaw.length === 0;
        if (needPlacements) {
          const dummyP = buildDummyPlacements();
          setGdnPlacementsRaw(dummyP);
          setGdnPlacements(aggregateGdnPlacements(dummyP));
        }
      }
    } catch (e) {
      // swallow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, displayCampaignsData, gdnPlacementsRaw]);

  // Normalize placement value to a canonical domain and href
  const normalizePlacementDomain = (value) => {
    if (!value) return { domain: '', href: '' };
    const raw = String(value).trim();
    try {
      const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
      const host = (u.hostname || '').replace(/^www\./i, '');
      return { domain: host, href: `${u.protocol}//${u.hostname}` };
    } catch (e) {
      const stripped = raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
      const head = stripped.split('/')[0];
      return { domain: head, href: head ? `https://${head}` : '' };
    }
  };

  // Aggregate multiple rows per placement into a single row per domain
  const aggregateGdnPlacements = (list) => {
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((p) => {
      const label = p?.placement_group_url || p?.placement || p?.placement_display_name || '';
      const { domain, href } = normalizePlacementDomain(label);
      if (!domain) return; // skip empty
      const key = domain;
      const impressions = Number(p?.impressions || 0);
      const clicks = Number(p?.clicks || 0);
      const cost = Number(p?.cost || 0);
      if (!map.has(key)) {
        map.set(key, { domain, href, impressions: 0, clicks: 0, cost: 0 });
      }
      const agg = map.get(key);
      agg.impressions += impressions;
      agg.clicks += clicks;
      agg.cost += cost;
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
  };

  // Aggregate by placement_type for right-side performance table
  const aggregatePlacementTypes = (list) => {
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((p) => {
      const type = ((p?.placement_type || 'UNKNOWN').toString()).replace(/^PlacementType\./, '');
      const impressions = Number(p?.impressions || 0);
      const clicks = Number(p?.clicks || 0);
      const cost = Number(p?.cost || 0);
      if (!map.has(type)) {
        map.set(type, { type, impressions: 0, clicks: 0, cost: 0 });
      }
      const agg = map.get(type);
      agg.impressions += impressions;
      agg.clicks += clicks;
      agg.cost += cost;
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
  };

  const togglePlacementDomain = (domain) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  // Build campaign breakdown per domain from raw rows
  const getCampaignBreakdownForDomain = (domain) => {
    if (!domain) return [];
    const map = new Map();
    (filteredGdnPlacementsRaw || []).forEach((row) => {
      const label = row?.placement_group_url || row?.placement || row?.placement_display_name || '';
      const { domain: d } = normalizePlacementDomain(label);
      if (d !== domain) return;
      const campaignName = row?.campaign_name || 'Unknown Campaign';
      const impressions = Number(row?.impressions || 0);
      const clicks = Number(row?.clicks || 0);
      const cost = Number(row?.cost || 0);
      if (!map.has(campaignName)) {
        map.set(campaignName, { campaignName, impressions: 0, clicks: 0, cost: 0 });
      }
      const agg = map.get(campaignName);
      agg.impressions += impressions;
      agg.clicks += clicks;
      agg.cost += cost;
    });
    return Array.from(map.values()).sort((a, b) => b.cost - a.cost);
  };

  const computeGdnTotals = (list) => {
    let impressions = 0, clicks = 0, cost = 0;
    (Array.isArray(list) ? list : []).forEach((p) => {
      impressions += Number(p?.impressions || 0);
      clicks += Number(p?.clicks || 0);
      cost += Number(p?.cost || 0);
    });
    const cpc = clicks > 0 ? cost / clicks : 0;
    const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0;
    return { impressions, clicks, cost, cpc, cpm };
  };

  // Segments için state'ler
  const [selectedSegment, setSelectedSegment] = useState('none');
  const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);
  const [dailyDataMap, setDailyDataMap] = useState(new Map());
  const dailyTableRef = useRef(null);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignSearchMode, setCampaignSearchMode] = useState('contains'); // 'contains' | 'not'

  // Local YYYY-MM-DD formatter (avoid timezone shifts)
  const formatLocalYMD = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // GA4 metrics by campaign (aggregated from rawData)
  const ga4ByCampaign = useMemo(() => {
    const map = {};
    const raw = displayCampaignsData?.rawData || [];
    raw.forEach(item => {
      const id = item.campaignId || item.campaign_id;
      if (!id) return;
      if (!map[id]) map[id] = { ga4Revenue: 0, ga4Transactions: 0 };
      map[id].ga4Revenue += Number(item.ga4Revenue || item.ga4_revenue || 0);
      map[id].ga4Transactions += Number(item.ga4Transaction || item.ga4_transaction || 0);
    });
    return map;
  }, [displayCampaignsData]);

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e) => {
    e.stopPropagation();
    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });
  };

  // Normalize campaign status to 'ENABLED' or 'PAUSED'
  const cleanStatus = (s) => {
    if (!s) return '';
    const cleaned = s.replace('CampaignStatus.', '').trim().toUpperCase();
    if (cleaned === 'ACTIVE' || cleaned === 'ENABLED') return 'ENABLED';
    if (cleaned === 'PAUSED') return 'PAUSED';
    return cleaned;
  };

  // Update campaign status or budget via live Google Ads API
  const updateEntity = useCallback(async (entityType, entityId, field, value, campaignId, oldValue, entityName) => {
    setUpdatingId(campaignId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityType, entityId, field, value, oldValue, entityName }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        toast.success('Updated successfully');
        setLiveDataMap(prev => {
          const key = String(campaignId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'amount_micros') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 1000000 } };
          }
          return prev;
        });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId, campaignName) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const micros = Math.round(newValue * 1000000);
    if (isNaN(micros) || micros <= 0) {
      toast.error('Invalid budget amount');
      return;
    }
    const live = liveDataMap[String(campaignId)];
    if (!live?.budgetId) {
      toast.error('Budget ID not found for this campaign');
      return;
    }
    const oldBudgetMicros = live.budget ? Math.round(live.budget * 1000000) : null;
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity('budget', live.budgetId, 'amount_micros', micros, campaignId, oldBudgetMicros ? String(oldBudgetMicros) : null, campaignName || null);
        setConfirmDialog(null);
      }
    });
  }, [editBudget, liveDataMap, updateEntity]);

  // Fetch change history data
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Fetch live status + daily budget from Google Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/google-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        credentials: 'include'
      });
      const data = await response.json();
      console.log('[DisplayCampaigns] campaigns-live response:', { ok: response.ok, success: data.success, count: data.campaigns?.length, error: data.error });
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budgetId: c.budgetId || null
            };
          }
        });
        console.log('[DisplayCampaigns] liveDataMap keys:', Object.keys(map));
        setLiveDataMap(map);
      } else {
        console.warn('[DisplayCampaigns] campaigns-live failed:', { status: response.status, error: data.error });
      }
    } catch (e) {
      console.error('[DisplayCampaigns] campaigns-live fetch error:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alan üzerine tıklandığında sıralama yönünü değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni bir alan seçildiğinde, varsayılan olarak azalan sıralama yap
      setSortField(field);
      setSortDirection(field === 'budget' ? 'desc' : 'asc');
    }
  };

  // Anahtar kelimeleri sırala
  const sortKeywords = (keywords, field, direction) => {
    if (!field) return keywords;

    return [...keywords].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Metin değerleri için sıralama (anahtar kelime metni, durum vb.)
      if (['text', 'status'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();

        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // Function to organize keyword data into campaigns and ad groups
  const organizeKeywordData = (data) => {
    if (!data || data.length === 0) {
      console.log('No keyword data available');
      return { campaigns: [] };
    }

    console.log(`Organizing ${data.length} keyword records`);
    
    // 🔍 DEBUG: Raw data sample'ını kontrol et
    console.log('📊 Raw data sample:', data.slice(0, 3));
    console.log('📅 Date field samples:', data.slice(0, 5).map(item => ({
      campaign_id: item.campaign_id,
      campaign_name: item.campaign_name,
      date: item.date,
      cost: item.cost
    })));

    // Kampanya ve reklam grupları için map'ler oluştur
    const campaignMap = new Map();
    const adGroupMap = new Map();
    
    // Günlük veriler için map oluştur - campaignId_date key ile
    const dailyDataMapLocal = new Map();
    
    // Anahtar kelimeleri birleştirmek için bir map oluştur
    const keywordMap = new Map();

    // Önce tüm kampanya ve reklam gruplarını oluştur
    data.forEach(item => {
      const campaignId = item.campaign_id;
      const adGroupId = item.ad_group_id;
      const keywordId = item.keyword_id;
      const keywordText = item.keyword_text?.toLowerCase() || '';
      const date = item.date;
      // Normalize date to YYYY-MM-DD to avoid timezone and time part mismatches
      const normalizedDate = date ? new Date(date).toISOString().split('T')[0] : null;
      
      // 🗓️ Günlük veriler için daily data map'i oluştur
      if (normalizedDate) {
        const dailyKey = `${campaignId}_${normalizedDate}`;
        if (!dailyDataMapLocal.has(dailyKey)) {
          dailyDataMapLocal.set(dailyKey, {
            campaignId: campaignId,
            date: normalizedDate,
            name: item.campaign_name || 'Unknown Campaign',
            status: item.campaign_status || 'Unknown',
            budget: parseFloat(item.campaign_budget || 0),
            spend: parseFloat(item.cost || 0),
            impressions: parseInt(item.impressions || 0, 10),
            clicks: parseInt(item.clicks || 0, 10),
            conversions: parseInt(item.conversions || 0, 10),
            conversionValue: parseFloat(item.conversion_value || 0)
          });
        } else {
          // Aggregate if multiple entries for same campaign-date
          const dailyData = dailyDataMapLocal.get(dailyKey);
          dailyData.spend += parseFloat(item.cost || 0);
          dailyData.impressions += parseInt(item.impressions || 0, 10);
          dailyData.clicks += parseInt(item.clicks || 0, 10);
          dailyData.conversions += parseInt(item.conversions || 0, 10);
          dailyData.conversionValue += parseFloat(item.conversion_value || 0);
        }
      }

      // Kampanyayı ekle
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: item.campaign_name || 'Unknown Campaign',
          status: item.campaign_status || 'Unknown',
          budget: parseFloat(item.campaign_budget || 0),
          spend: 0, // Reklam gruplarından hesaplanacak
          impressions: 0, // Reklam gruplarından hesaplanacak
          clicks: 0, // Reklam gruplarından hesaplanacak
          conversions: 0, // Reklam gruplarından hesaplanacak
          conversionValue: 0, // Reklam gruplarından hesaplanacak
          adGroups: [],
          expanded: true
        });
      }

      // Reklam grubunu ekle
      if (!adGroupMap.has(adGroupId)) {
        adGroupMap.set(adGroupId, {
          id: adGroupId,
          name: item.ad_group_name || 'Unknown Ad Group',
          status: item.ad_group_status || 'Unknown',
          campaignId: campaignId,
          spend: 0, // Anahtar kelimelerden hesaplanacak
          impressions: 0, // Anahtar kelimelerden hesaplanacak
          clicks: 0, // Anahtar kelimelerden hesaplanacak
          conversions: 0, // Anahtar kelimelerden hesaplanacak
          conversionValue: 0, // Anahtar kelimelerden hesaplanacak
          keywords: [],
          expanded: false
        });
      }

      // Anahtar kelime için benzersiz bir kimlik oluştur (reklam grubu + anahtar kelime metni)
      const uniqueKeywordId = `${adGroupId}_${keywordText}`;
      
      // Anahtar kelimeyi ekle veya güncelle
      if (!keywordMap.has(uniqueKeywordId)) {
        keywordMap.set(uniqueKeywordId, {
          id: keywordId,
          text: keywordText,
          status: item.keyword_status || 'Unknown',
          adGroupId: adGroupId,
          matchType: item.keyword_match_type || 'Unknown',
          spend: parseFloat(item.cost || 0),
          impressions: parseInt(item.impressions || 0, 10),
          clicks: parseInt(item.clicks || 0, 10),
          conversions: parseInt(item.conversions || 0, 10),
          conversionValue: parseFloat(item.conversion_value || 0),
          matchTypes: [item.keyword_match_type || 'Unknown']
        });
      } else {
        // Mevcut anahtar kelimeyi güncelle (metrikleri topla)
        const existingKeyword = keywordMap.get(uniqueKeywordId);
        existingKeyword.spend += parseFloat(item.cost || 0);
        existingKeyword.impressions += parseInt(item.impressions || 0, 10);
        existingKeyword.clicks += parseInt(item.clicks || 0, 10);
        existingKeyword.conversions += parseInt(item.conversions || 0, 10);
        existingKeyword.conversionValue += parseFloat(item.conversion_value || 0);
        
        // Eşleşme türünü ekle (eğer zaten yoksa)
        if (item.keyword_match_type && !existingKeyword.matchTypes.includes(item.keyword_match_type)) {
          existingKeyword.matchTypes.push(item.keyword_match_type);
        }
      }
    });

    // Anahtar kelimeleri reklam gruplarına ekle ve metrikleri hesapla
    adGroupMap.forEach(adGroup => {
      // Anahtar kelimeleri reklam gruplarına ekle
      const adGroupKeywords = Array.from(keywordMap.values())
        .filter(keyword => keyword.adGroupId === adGroup.id);
      
      adGroup.keywords = adGroupKeywords;
      
      // Anahtar kelimelerden reklam grubu metriklerini hesapla
      adGroupKeywords.forEach(keyword => {
        adGroup.spend += keyword.spend || 0;
        adGroup.impressions += keyword.impressions || 0;
        adGroup.clicks += keyword.clicks || 0;
        adGroup.conversions += keyword.conversions || 0;
        adGroup.conversionValue += keyword.conversionValue || 0;
      });
      
      // CTR, CPC, dönüşüm oranı vb. hesapla
      adGroup.ctr = adGroup.impressions > 0 ? (adGroup.clicks / adGroup.impressions) * 100 : 0;
      adGroup.cpc = adGroup.clicks > 0 ? adGroup.spend / adGroup.clicks : 0;
      adGroup.conversionRate = adGroup.clicks > 0 ? (adGroup.conversions / adGroup.clicks) * 100 : 0;
      adGroup.costPerConversion = adGroup.conversions > 0 ? adGroup.spend / adGroup.conversions : 0;
      adGroup.roas = adGroup.spend > 0 ? adGroup.conversionValue / adGroup.spend : 0;
      
      // Reklam grubunu ilgili kampanyaya ekle
      const campaign = campaignMap.get(adGroup.campaignId);
      if (campaign) {
        campaign.adGroups.push(adGroup);
        
        // Reklam grubu metriklerini kampanya metriklerine ekle
        campaign.spend += adGroup.spend || 0;
        campaign.impressions += adGroup.impressions || 0;
        campaign.clicks += adGroup.clicks || 0;
        campaign.conversions += adGroup.conversions || 0;
        campaign.conversionValue += adGroup.conversionValue || 0;
      }
    });

    // Günlük verilerin metriklerini hesapla
    dailyDataMapLocal.forEach(dailyData => {
      dailyData.ctr = dailyData.impressions > 0 ? (dailyData.clicks / dailyData.impressions) * 100 : 0;
      dailyData.cpc = dailyData.clicks > 0 ? dailyData.spend / dailyData.clicks : 0;
      dailyData.conversionRate = dailyData.clicks > 0 ? (dailyData.conversions / dailyData.clicks) * 100 : 0;
      dailyData.costPerConversion = dailyData.conversions > 0 ? dailyData.spend / dailyData.conversions : 0;
      dailyData.roas = dailyData.spend > 0 ? dailyData.conversionValue / dailyData.spend : 0;
    });

    // Kampanya metriklerini hesapla
    campaignMap.forEach(campaign => {
      // CTR, CPC, dönüşüm oranı vb. hesapla
      campaign.ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0;
      campaign.cpc = campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0;
      campaign.conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0;
      campaign.costPerConversion = campaign.conversions > 0 ? campaign.spend / campaign.conversions : 0;
      campaign.roas = campaign.spend > 0 ? campaign.conversionValue / campaign.spend : 0;
      
      // Kampanya gelirini conversionValue olarak ayarla
      campaign.revenue = campaign.conversionValue;
    });

    // Daily data map'i state'e kaydet
    setDailyDataMap(dailyDataMapLocal);

    // 🔍 DEBUG: Günlük veriler için log ekle
    console.log('📊 Daily Data Map Debug:');
    console.log('Map size:', dailyDataMapLocal.size);
    console.log('Sample daily keys:', Array.from(dailyDataMapLocal.keys()).slice(0, 5));
    console.log('Sample daily data:', Array.from(dailyDataMapLocal.values()).slice(0, 2));

    // Kampanyaları bir diziye dönüştür
    const campaigns = Array.from(campaignMap.values());
    
    console.log(`Organized ${campaigns.length} campaigns with ${adGroupMap.size} ad groups and ${keywordMap.size} unique keywords and ${dailyDataMapLocal.size} daily records`);
    
    return { campaigns };
  };

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        cpc: 0,
        conversionRate: 0,
        cost: 0,
        roas: 0
      };
    }

    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let totalRevenue = 0;

    campaigns.forEach(campaign => {
      totalClicks += campaign.clicks || 0;
      totalCost += campaign.spend || 0;
      totalConversions += campaign.conversions || 0;
      totalRevenue += campaign.revenue || 0;
    });

    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;

    return {
      cpc: avgCpc,
      conversionRate: conversionRate,
      cost: totalCost,
      roas: roas
    };
  };

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true); // Veri yüklenmeye başlarken true yap
        // Fetch keyword data from database
        await fetchKeywordData();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to fetch campaign data');
      } finally {
        setDataLoading(false); // Veri yükleme bittiğinde false yap
      }
    };

    fetchData();
  }, [dateRange, selectedAccountId, gaAccountFilter]);

  // Process keyword data when it changes
  useEffect(() => {
    if (keywordData && Array.isArray(keywordData) && keywordData.length > 0) {
      console.log('Processing keyword data...');
      try {
        const { campaigns: fetchedCampaigns } = organizeKeywordData(keywordData);
        
        // Update state with fetched data - use startTransition for non-blocking updates
        startTransition(() => {
        setCampaigns(fetchedCampaigns);
        // Başlangıçta tüm kampanyalar kapalı olsun
        setExpandedCampaigns(new Set());
        });
        
        // Log the organized data
        console.log('Organized campaigns:', fetchedCampaigns);
        
        // Remove the call to generateTrendDataFromKeywords that's causing errors
        // generateTrendDataFromKeywords();
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing keyword data:', error);
        // Remove the toast error message
        // toast.error('Error processing campaign data');
        setIsLoading(false);
      }
    }
  }, [keywordData]);

  // Function to fetch keyword data from the database
  const fetchKeywordData = async () => {
    try {
      setIsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoading(false);
        return;
      }

      console.log('Fetching keyword data for brand ID:', brandId);
      
      // Calculate date range based on selected filter
      const dateRangeParams = calculateDateRange(dateRange);
      console.log('Date range params:', dateRangeParams);
      
      // Update the API URL to match the backend URL structure
      let apiUrl = `https://api.glowytics.com/api/google-ads/keyword-full-performance?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const sd = formatDateYMD(customStartDate);
        const ed = formatDateYMD(customEndDate);
        apiUrl += `&startDate=${sd}&endDate=${ed}`;
      }
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to fetch keyword data: ${errorData.message || response.statusText}`);
      }

      let data = await response.json();
      // For the specified brand, always use dummy campaigns to ensure populated UI
      if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC) {
        data = buildDummyDisplayData(dateRange);
      }
      console.log('Keyword data fetched successfully from google_ads_keyword_full_performance view:', data);
      
      // Log the first few records to inspect the structure
      if (data && data.length > 0) {
        console.log('Sample data (first 3 records):', data.slice(0, 3));
        
        // Filter data based on date range if needed
        const filteredData = filterDataByDateRange(data, dateRangeParams);
        
        // Group data by date and calculate total cost for each date
        groupKeywordDataByDate(filteredData);
      } else {
        console.log('No data returned from API');
      }
      
      // Use startTransition for non-blocking UI updates
      startTransition(() => {
      setKeywordData(data);
      });
      
      // Try-catch block for fetchPerformanceTrend to prevent errors
      try {
        // Ayrıca son 30 günlük trend verilerini çekelim
        await fetchPerformanceTrend();
      } catch (error) {
        console.error("Error in fetchPerformanceTrend:", error);
        // Don't show error toast
      }
      
      // Call the new function to fetch display campaigns data
      await fetchDisplayCampaigns();
      
      setIsLoading(false); // Make sure to set loading to false here
    } catch (error) {
      console.error('Error fetching keyword data:', error);
      // Remove toast error message
      // toast.error(`Failed to fetch keyword data: ${error.message}`);
      setIsLoading(false);
    }
  };
  
  // Yeni eklenen fonksiyon: Display Campaign verilerini çekmek için
  const fetchDisplayCampaigns = async () => {
    try {
      console.log('========== FETCHING DISPLAY CAMPAIGNS DATA ==========');
      console.log('Date range:', dateRange);
      
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('❌ No brand ID found in localStorage');
        return;
      }
      // Immediate dummy short-circuit for demo/dummy brand
      if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC) {
        const dummy = buildDummyDisplayData(dateRange);
        setDisplayCampaignsData(dummy);
        updateDisplayCampaignsChart(dummy.dailyTrend || []);
        const dummyPlacements = buildDummyPlacements();
        setGdnPlacementsRaw(dummyPlacements);
        setGdnPlacements(aggregateGdnPlacements(dummyPlacements));
        console.log('✅ Short-circuited to dummy display data for demo brand');
        return;
      }
      
      console.log('Brand ID:', brandId);
      
      // Calculate date range parameters
      const dateRangeParams = calculateDateRange(dateRange);
      console.log('Date range params:', dateRangeParams);
      
      // Endpoint: display vs demand-gen — choose by current route
      const isDemandGen = (location?.pathname || '').includes('/demand-gen');
      const endpointSlug = isDemandGen ? 'demand-gen-campaigns' : 'display-campaigns';
      // ga4Transactions bilgisini de içeren alanları backend döndürmeli: ga4Transactions | ga4_transactions
      const qs = new URLSearchParams();
      qs.set('dateRange', dateRange);
      qs.set('includeGa4', '1');
      if (dateRangeParams?.startDate && dateRangeParams?.endDate) {
        qs.set('startDate', dateRangeParams.startDate);
        qs.set('endDate', dateRangeParams.endDate);
      }
      if (gaAccountFilter !== 'all') qs.set('accountId', gaAccountFilter);
      const apiUrl = `https://api.glowytics.com/api/google-ads/${endpointSlug}?${qs.toString()}`;
      console.log('API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Display Campaigns API Error:', response.status, response.statusText);
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch display campaigns data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Console'a sonuçları bas
      console.log(`========== ${isDemandGen ? 'DEMAND GEN' : 'DISPLAY'} CAMPAIGNS DATA ==========`);
      console.log('✅ Raw response received');
      console.log('🔍 FULL API RESPONSE:', data);
      console.log('Response structure:', {
        campaigns: data.campaigns ? `${data.campaigns.length} campaigns` : 'No campaigns',
        dailyTrend: data.dailyTrend ? `${data.dailyTrend.length} data points` : 'No daily trend',
        totalMetrics: data.totalMetrics ? 'Available' : 'Not available',
        rawData: data.rawData ? `${data.rawData.length} raw records` : 'No raw data'
      });
      
      // Derive CTR and CPC on the frontend for each campaign to ensure table shows correct values
      if (Array.isArray(data.campaigns)) {
        data.campaigns = data.campaigns.map((c) => {
          const impressions = Number(c.impressions || 0);
          const clicks = Number(c.clicks || 0);
          const cost = Number(c.cost ?? c.spend ?? 0);
          const conversions = Number(c.conversions || 0);
          const revenue = Number(c.revenue ?? c.conversionValue ?? 0);

          const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
          const cpc = clicks > 0 ? (cost / clicks) : 0;
          const conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
          const roas = cost > 0 ? (revenue / cost) : 0;

          return {
            ...c,
            ctr,
            cpc,
            conversionRate: c.conversionRate ?? conversionRate,
            roas: c.roas ?? roas,
            spend: c.spend ?? cost, // normalize if downstream expects spend
          };
        });
      }
      
      if (data.campaigns && data.campaigns.length > 0) {
        console.log('📊 Campaign data sample:', data.campaigns[0]);
        console.log(`📈 Total campaigns: ${data.campaigns.length}`);
        
        // Log campaign metrics summary
        const totalCost = data.campaigns.reduce((sum, c) => sum + (c.cost || c.spend || 0), 0);
        const totalRevenue = data.campaigns.reduce((sum, c) => sum + (c.revenue || c.conversionValue || 0), 0);
        const totalImpressions = data.campaigns.reduce((sum, c) => sum + (c.impressions || 0), 0);
        const totalClicks = data.campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
        
        console.log('Campaign Totals:', {
          cost: totalCost.toFixed(2),
          revenue: totalRevenue.toFixed(2),
          impressions: totalImpressions,
          clicks: totalClicks,
          roas: totalCost > 0 ? (totalRevenue / totalCost).toFixed(2) : '0'
        });
      } else {
        console.log('⚠️ No campaign data available');
      }
      
      if (data.dailyTrend && data.dailyTrend.length > 0) {
        console.log('📈 Daily trend data available:', data.dailyTrend.length, 'data points');
        console.log('Date range in trend:', 
          data.dailyTrend[0]?.date, 'to', 
          data.dailyTrend[data.dailyTrend.length - 1]?.date
        );
        console.log('🔍 FIRST 3 DAILY TREND SAMPLES:', data.dailyTrend.slice(0, 3));
        console.log('🗂️ Daily trend data keys:', Object.keys(data.dailyTrend[0] || {}));
        console.log('Sample daily trend data:', data.dailyTrend[0]);
        
        // Log trend summary
        const totalTrendCost = data.dailyTrend.reduce((sum, d) => sum + (Number(d.cost) || 0), 0);
        const totalTrendRevenue = data.dailyTrend.reduce((sum, d) => sum + (Number(d.revenue) || 0), 0);
        console.log('Daily Trend Totals:', {
          cost: totalTrendCost.toFixed(2),
          revenue: totalTrendRevenue.toFixed(2)
        });
      } else {
        console.log('⚠️ No daily trend data available');
      }
      
      if (data.totalMetrics) {
        console.log('📊 Total metrics:', data.totalMetrics);
      }
      
      console.log('========== END OF DISPLAY CAMPAIGNS DATA ==========');
      
      // State'i güncelle - use startTransition for non-blocking UI updates
      startTransition(() => {
      setDisplayCampaignsData(data);
      });

      // Build dailyDataMap from raw display data to drive per-campaign daily breakdown
      try {
        const raw = Array.isArray(data.rawData) ? data.rawData : [];
        const localDaily = new Map();
        raw.forEach(rec => {
          const cid = rec.campaignId || rec.campaign_id || rec.campaignid;
          const dateField = rec.date || rec.day || rec.reportDate || rec.report_date;
          const d = dateField ? new Date(dateField) : null;
          if (!cid || !d) return;
          const keyDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString().split('T')[0];
          const key = `${cid}_${keyDate}`;
          if (!localDaily.has(key)) {
            localDaily.set(key, {
              campaignId: cid,
              date: keyDate,
              name: rec.campaignName || rec.campaign_name || 'Unknown Campaign',
              status: rec.status || rec.campaign_status || 'Unknown',
              budget: Number(rec.campaign_budget || 0),
              spend: Number(rec.cost || rec.google_ads_cost || 0),
              impressions: Number(rec.impressions || rec.google_ads_impression || 0),
              clicks: Number(rec.clicks || rec.google_ads_clicks || 0),
              conversions: Number(rec.conversions || rec.transactions || rec.google_ads_transaction || 0),
              conversionValue: Number(rec.conversion_value || rec.revenue || rec.google_ads_revenue || 0),
              ga4Revenue: Number(rec.ga4Revenue || rec.ga4_revenue || 0),
              ga4Transactions: Number(rec.ga4Transactions || rec.ga4_transactions || 0),
              sessions: Number(rec.sessions || 0)
            });
          } else {
            const cur = localDaily.get(key);
            cur.spend += Number(rec.cost || rec.google_ads_cost || 0);
            cur.impressions += Number(rec.impressions || rec.google_ads_impression || 0);
            cur.clicks += Number(rec.clicks || rec.google_ads_clicks || 0);
            cur.conversions += Number(rec.conversions || rec.transactions || rec.google_ads_transaction || 0);
            cur.conversionValue += Number(rec.conversion_value || rec.revenue || rec.google_ads_revenue || 0);
            cur.ga4Revenue += Number(rec.ga4Revenue || rec.ga4_revenue || 0);
            cur.ga4Transactions += Number(rec.ga4Transactions || rec.ga4_transactions || 0);
            cur.sessions += Number(rec.sessions || 0);
          }
        });
        // Derive rate metrics for each daily record to avoid undefined toFixed errors
        localDaily.forEach((val) => {
          const impressions = Number(val.impressions || 0);
          const clicks = Number(val.clicks || 0);
          const spend = Number(val.spend || 0);
          const conversions = Number(val.conversions || 0);
          const revenue = Number(val.conversionValue || 0);
          val.ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
          val.cpc = clicks > 0 ? (spend / clicks) : 0;
          val.conversionRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
          val.costPerConversion = conversions > 0 ? (spend / conversions) : 0;
          val.roas = spend > 0 ? (revenue / spend) : 0;
        });
        setDailyDataMap(localDaily);
      } catch (e) {
        console.warn('Failed to build dailyDataMap from display rawData:', e);
      }

      // Grafiği güncelle (her zaman çağır, boş data ile bile)
      updateDisplayCampaignsChart(data.dailyTrend || []);
      
      console.log('✅ Display campaigns data fetch completed successfully');
      
      // Fetch GDN placements for the same date range
      try {
        const placementsQs = new URLSearchParams();
        placementsQs.set('dateRange', dateRange);
        placementsQs.set('includeTotals', '1');
        if (gaAccountFilter !== 'all') placementsQs.set('accountId', gaAccountFilter);
        // Custom range params
        const range = calculateDateRange(dateRange);
        if (range?.startDate && range?.endDate) {
          placementsQs.set('startDate', range.startDate);
          placementsQs.set('endDate', range.endDate);
        }
        const placementsUrl = `https://api.glowytics.com/api/google-ads/gdn-placements?${placementsQs.toString()}`;
        console.log('GDN Placements API URL:', placementsUrl);
        const placementsRes = await fetch(placementsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (placementsRes.ok) {
          const placementsPayload = await placementsRes.json();
          const placements = Array.isArray(placementsPayload?.rows) ? placementsPayload.rows : placementsPayload;
          const placementTotals = placementsPayload?.totals || null;
          if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC) {
            const dummy = buildDummyPlacements();
            setGdnPlacementsRaw(dummy);
            setGdnPlacements(aggregateGdnPlacements(dummy));
          } else {
            setGdnPlacementsRaw(Array.isArray(placements) ? placements : []);
            const aggregated = aggregateGdnPlacements(placements);
            setGdnPlacements(aggregated);
            if (placementTotals) {
              setGdnPlacementTotals({
                impressions: placementTotals.impressions || 0,
                clicks: placementTotals.clicks || 0,
                cost: placementTotals.cost || 0,
                conversions: placementTotals.conversions || 0,
                conversion_value: placementTotals.conversion_value || 0,
              });
            } else {
              setGdnPlacementTotals(null);
            }
          }
          console.log('GDN placements fetched:', (placements?.length || 0));
        } else {
          const txt = await placementsRes.text();
          console.warn('GDN placements fetch failed:', placementsRes.status, txt);
          if (brandId === DUMMY_BRAND_ID || brandId === DEMO_BRAND_ID_SC) {
            const dummy = buildDummyPlacements();
            setGdnPlacementsRaw(dummy);
            setGdnPlacements(aggregateGdnPlacements(dummy));
            setGdnPlacementTotals(null);
          } else {
            setGdnPlacements([]);
            setGdnPlacementsRaw([]);
            setGdnPlacementTotals(null);
          }
        }
      } catch (e) {
        console.warn('GDN placements fetch error:', e);
        setGdnPlacements([]);
        setGdnPlacementsRaw([]);
        setGdnPlacementTotals(null);
      }
    } catch (error) {
      console.error('❌ Error fetching display campaigns data:', error);
      console.error('Error details:', error.message);
      
      const currentBrand = localStorage.getItem('currentBrandId');
      if (currentBrand === DUMMY_BRAND_ID || currentBrand === DEMO_BRAND_ID_SC) {
        const dummy = buildDummyDisplayData(dateRange);
        setDisplayCampaignsData(dummy);
        updateDisplayCampaignsChart(dummy.dailyTrend || []);
        const dummyPlacements = buildDummyPlacements();
        setGdnPlacementsRaw(dummyPlacements);
        setGdnPlacements(aggregateGdnPlacements(dummyPlacements));
      } else {
        setDisplayCampaignsData({
          campaigns: [],
          dailyTrend: [],
          totalMetrics: {},
          rawData: []
        });
        updateDisplayCampaignsChart([]);
      }
    }
  };
  
  // Günlük trend verileriyle grafik güncelleme fonksiyonu
  const updateDisplayCampaignsChart = (dailyTrendData) => {
    if (!dailyTrendData || dailyTrendData.length === 0) {
      console.log("No daily trend data available to update chart");
      // Set empty chart data instead of returning
      setChartData({ labels: [], datasets: [] });
      return;
    }
    
    // Tarihleri formatlayarak etiketleri oluştur
      const labels = dailyTrendData.map(item => {
      const date = new Date(item.date);
      // Force UTC date to avoid off-by-one on local timezones
      const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      return utc.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
    
    // Maliyet ve gelir değerlerini çıkar
    const costs = dailyTrendData.map(item => Number(item.cost || 0));
    const revenues = dailyTrendData.map(item => Number(item.revenue || 0));
    
    console.log("🔍 Chart Debug Info:");
    console.log("📊 Daily trend data sample:", dailyTrendData.slice(0, 3));
    console.log("💰 Costs array:", costs.slice(0, 5));
    console.log("💵 Revenues array:", revenues.slice(0, 5));
    console.log("📅 Labels array:", labels.slice(0, 5));
    
    // Grafik verilerini güncelle
    const newChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Cost',
          data: costs,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        },
        {
          label: 'Revenue',
          data: revenues,
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        }
      ]
    };
    
    // Chart state'ini güncelle
    setChartData(newChartData);
    
    console.log("✅ Display campaigns chart updated successfully");
  };
  
  // Local date formatter to avoid TZ shift (YYYY-MM-DD)
  const formatLocalDate = (d) => {
    if (!d) return null;
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Update the calculateDateRange function to handle the "This Month" filter
  const calculateDateRange = (selectedRange) => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      // Support both '7days' and 'last7'
      case '7days':
      case 'last7':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      // Support both '30days' and 'last30'
      case '30days':
      case 'last30':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        console.log(`30 days range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
        break;
      case 'thisMonth':
        // First day of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Current day
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'lastMonth':
        // First day of last month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Last day of last month
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
        startDate = new Date(customStartDate.getFullYear(), customStartDate.getMonth(), customStartDate.getDate());
        endDate = new Date(customEndDate.getFullYear(), customEndDate.getMonth(), customEndDate.getDate(), 23, 59, 59);
          break;
        }
        // Fallthrough to default if custom not set yet
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
    }

    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  };
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, dateRange) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to end of day for end date for inclusive comparison
    endDate.setHours(23, 59, 59, 999);
    
    return data.filter(item => {
      if (!item.date) return false;
      
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  // Date-filtered GDN placements (falls back to raw if records lack date fields)
  const filteredGdnPlacementsRaw = useMemo(() => {
    const raw = Array.isArray(gdnPlacementsRaw) ? gdnPlacementsRaw : [];
    if (raw.length === 0) return raw;
    const range = calculateDateRange(dateRange);
    // If we cannot compute a range, return as-is
    if (!range?.startDate || !range?.endDate) return raw;
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    end.setHours(23, 59, 59, 999);
    const hasAnyDate = raw.some(r => r?.date || r?.day || r?.reportDate || r?.report_date);
    if (!hasAnyDate) return raw; // backend already aggregated per range
    return raw.filter(r => {
      const dStr = r?.date || r?.day || r?.reportDate || r?.report_date;
      if (!dStr) return false;
      const d = new Date(dStr);
      return d >= start && d <= end;
    });
  }, [gdnPlacementsRaw, dateRange, customStartDate, customEndDate]);

  // Aggregations based on filtered placements
  const filteredGdnPlacements = useMemo(() => aggregateGdnPlacements(filteredGdnPlacementsRaw), [filteredGdnPlacementsRaw]);
  const placementTypeRows = useMemo(() => aggregatePlacementTypes(filteredGdnPlacementsRaw), [filteredGdnPlacementsRaw]);
  
  // New function to group keyword data by date and calculate total cost
  const groupKeywordDataByDate = (keywordData) => {
    console.log("========== STARTING DAILY COST CALCULATION ==========");
    console.log("Input data count:", keywordData.length);
    
    // Log the structure of the first few records to understand the data format
    if (keywordData.length > 0) {
      // Log the keys of the first record to see what fields are available
      console.log("First record keys:", Object.keys(keywordData[0]));
      
      // Log the complete first record for inspection
      console.log("First record:", keywordData[0]);
      
      // Check if cost is in a different field
      if (keywordData[0].cost !== undefined) {
        console.log("Found 'cost' field instead of 'cost_micros'");
      }
      
      // Check for nested fields that might contain cost data
      for (const key in keywordData[0]) {
        const value = keywordData[0][key];
        if (value && typeof value === 'object') {
          console.log(`Checking nested object in field '${key}':`, value);
          
          // Check if this nested object has cost-related fields
          if (value.cost !== undefined || value.cost_micros !== undefined) {
            console.log(`Found cost data in nested field '${key}'`);
          }
        }
      }
    }
    
    // Create a map to store costs by date
    const costByDate = new Map();
    let processedRecords = 0;
    let recordsWithDate = 0;
    let recordsWithCost = 0;
    
    // Process each keyword
    keywordData.forEach(keyword => {
      processedRecords++;
      
      // Check if the keyword has a date field
      if (keyword.date) {
        recordsWithDate++;
        
        // Check for cost field (not cost_micros)
        if (keyword.cost !== undefined) {
          recordsWithCost++;
          const date = keyword.date;
          // Assuming cost is already in the correct format (not micros)
          const cost = parseFloat(keyword.cost);
          
          if (costByDate.has(date)) {
            costByDate.set(date, costByDate.get(date) + cost);
          } else {
            costByDate.set(date, cost);
          }
        }
      }
    });
    
    console.log("Processing stats:");
    console.log("- Total records processed:", processedRecords);
    console.log("- Records with date field:", recordsWithDate);
    console.log("- Records with cost field:", recordsWithCost);
    
    // Convert map to array of objects sorted by date
    const dailyCostArray = Array.from(costByDate.entries())
      .map(([date, cost]) => ({ date, cost }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log("========== DAILY COST DATA (GROUPED BY DATE) ==========");
    console.log(dailyCostArray);
    
    // Also log in table format for better readability
    console.log("========== DAILY COST DATA (TABLE FORMAT) ==========");
    console.table(dailyCostArray);
    
    console.log("Total unique dates:", dailyCostArray.length);
    console.log("Date range:", 
      dailyCostArray.length > 0 ? 
      `${dailyCostArray[0].date} to ${dailyCostArray[dailyCostArray.length-1].date}` : 
      "No data"
    );
    console.log("Total cost:", dailyCostArray.reduce((sum, item) => sum + item.cost, 0).toFixed(2));
    console.log("========== END OF DAILY COST CALCULATION ==========");
    
    // Note: Chart is updated by displayCampaigns data, not keyword data
    // updateDailyCostChart(dailyCostArray); // Commented out to prevent conflict
    
    return dailyCostArray;
  };
  
  // New function to update the chart with daily cost data
  const updateDailyCostChart = (dailyCostData) => {
    if (!dailyCostData || dailyCostData.length === 0) {
      console.log("No daily cost data available to update chart");
      return;
    }
    
    // Format dates for display (convert from ISO to readable format in English)
    const labels = dailyCostData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    });
    
    // Extract cost values
    const costs = dailyCostData.map(item => item.cost);
    
    // Create a new chart data object with daily costs
    const newChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Daily Cost',
          data: costs,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y',
        }
      ]
    };
    
    // Set the chart data
    setChartData(newChartData);
    
    console.log("Performance trend chart updated with daily cost data:", newChartData);
  };
  
  // Performans trend verilerini çekmek için yeni fonksiyon
  const fetchPerformanceTrend = async () => {
    try {
      setIsLoadingTrend(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoadingTrend(false);
        return;
      }
      
      // Use fetch API instead of axios
      const trendUrl = `https://api.glowytics.com/api/google-ads/performance-trend${gaAccountFilter !== 'all' ? '?accountId=' + gaAccountFilter : ''}`;
      const response = await fetch(trendUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Don't try to parse JSON if response is not OK
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch performance trend: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("API Performance Trend Response:", data);
      
      if (data && data.trend) {
        setTrendData(data.trend);
      } else {
        setTrendData(generateEmptyTrendData());
      }
      setIsLoadingTrend(false);
    } catch (error) {
      console.error("Error fetching performance trend:", error);
      setTrendData(generateEmptyTrendData());
      setIsLoadingTrend(false);
      // Don't show error toast
    }
  };
  
  // Keyword verilerinden trend verilerini oluşturan yardımcı fonksiyon
  const generateTrendDataFromKeywords = () => {
    try {
      if (!keywordData || !Array.isArray(keywordData) || keywordData.length === 0) {
        console.log("No keyword data available for trend generation");
        return;
      }

      // Create a map to store metrics by date
      const metricsByDate = new Map();

      // Process all keywords directly from keywordData
      keywordData.forEach(keyword => {
        if (keyword.date) {
          const date = keyword.date;
          
          if (!metricsByDate.has(date)) {
            metricsByDate.set(date, {
              date,
              impressions: 0,
              clicks: 0,
              cost: 0,
              conversions: 0,
              conversionValue: 0
            });
          }

          const metrics = metricsByDate.get(date);
          metrics.impressions += parseInt(keyword.impressions || 0, 10);
          metrics.clicks += parseInt(keyword.clicks || 0, 10);
          metrics.cost += parseFloat(keyword.cost || 0);
          metrics.conversions += parseInt(keyword.conversions || 0, 10);
          metrics.conversionValue += parseFloat(keyword.conversion_value || 0);
        }
      });

      // Convert to array and sort by date
      const trendData = Array.from(metricsByDate.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate derived metrics
      trendData.forEach(day => {
        day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
        day.cpc = day.clicks > 0 ? day.cost / day.clicks : 0;
        day.convRate = day.clicks > 0 ? (day.conversions / day.clicks) * 100 : 0;
        day.cpa = day.conversions > 0 ? day.cost / day.conversions : 0;
        day.roas = day.cost > 0 ? day.conversionValue / day.cost : 0;
      });

      console.log("Generated trend data from keywords:", trendData);
      
      return trendData;
    } catch (error) {
      console.error("Error generating trend data:", error);
      return [];
    }
  };
  
  // Boş trend verisi oluşturan yardımcı fonksiyon
  const generateEmptyTrendData = () => {
    console.log("Generating empty trend data for the last 30 days");
    
    // Son 30 günün tarihlerini oluştur
    const labels = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      // Tarihi gün-ay formatında göster
      labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    }
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Spend',
          data: Array(30).fill(0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: Array(30).fill(0),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null) return '0';
    
    const {
      prefix = '',
      suffix = '',
      minimumFractionDigits = 0,
      maximumFractionDigits = 0,
      notation = 'standard'
    } = options;
    
    // Remove any existing currency symbol from the value if it's a string
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/[₺$€£¥]/g, '');
    }
    
    // Convert to number
    numericValue = parseFloat(numericValue);
    
    // Format the number
    return `${prefix}${numericValue.toLocaleString(undefined, {
      minimumFractionDigits,
      maximumFractionDigits,
      notation
    })}${suffix}`;
  };

  // Kampanyaları sırala
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    // Synthetic combined revenue (GA4 + Adjust)
    if (sortField === 'totalCombinedRevenue') {
      const aValue = (Number(ga4ByCampaign[a.campaignId]?.ga4Revenue) || 0) + (Number(a.adjustAllRevenue) || 0);
      const bValue = (Number(ga4ByCampaign[b.campaignId]?.ga4Revenue) || 0) + (Number(b.adjustAllRevenue) || 0);
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    // Sayısal değerler için sıralama
    if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'conversionValue', 'roas'].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(a[sortField]) || 0;
      const bValue = parseFloat(b[sortField]) || 0;

      return sortDirection === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'text'].includes(sortField)) {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak budget'a göre azalan sıralama
    return b.budget - a.budget;
  });

  // Eğer displayCampaignsData varsa, onun campaign verilerini kullan
  const displayCampaigns = displayCampaignsData?.campaigns || [];
  
  // Display kampanyalarını da sırala
  const sortedDisplayCampaigns = displayCampaigns.length > 0 ? [...displayCampaigns].sort((a, b) => {
    // Sayısal değerler için sıralama
    if ([
      'spend',
      'budget',
      'impressions',
      'clicks',
      'ctr',
      'cpc',
      'conversions',
      'transactions',
      'ga4Transactions',
      'conversionRate',
      'revenue',
      'roas',
      'adjustSessions',
      'adjustInstalls',
      'adjustPurchaseEvents',
      'adjustAllRevenue'
    ].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(
        a[sortField] ?? (
          sortField === 'conversions'
            ? (a.transactions ?? a.google_ads_transaction)
            : sortField === 'ga4Transactions'
              ? (a.ga4Transactions ?? a.ga4_transactions ?? (ga4ByCampaign[a.campaignId]?.ga4Transactions))
              : sortField === 'adjustSessions'
                ? a.adjustSessions
                : sortField === 'adjustInstalls'
                  ? a.adjustInstalls
                  : sortField === 'adjustPurchaseEvents'
                    ? a.adjustPurchaseEvents
                    : sortField === 'adjustAllRevenue'
                      ? a.adjustAllRevenue
              : 0
        )
      ) || 0;
      const bValue = parseFloat(
        b[sortField] ?? (
          sortField === 'conversions'
            ? (b.transactions ?? b.google_ads_transaction)
            : sortField === 'ga4Transactions'
              ? (b.ga4Transactions ?? b.ga4_transactions ?? (ga4ByCampaign[b.campaignId]?.ga4Transactions))
              : sortField === 'adjustSessions'
                ? b.adjustSessions
                : sortField === 'adjustInstalls'
                  ? b.adjustInstalls
                  : sortField === 'adjustPurchaseEvents'
                    ? b.adjustPurchaseEvents
                    : sortField === 'adjustAllRevenue'
                      ? b.adjustAllRevenue
              : 0
        )
      ) || 0;
      
      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'campaignName'].includes(sortField)) {
      const aValue = String(a[sortField] || a.campaignName || '').toLowerCase();
      const bValue = String(b[sortField] || b.campaignName || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak cost'a göre azalan sıralama
    return b.cost - a.cost;
  }) : [];

  // Adjust metrikleri var mı kontrol et (Search sayfasındaki gibi)
  const hasAdjust = useMemo(() => {
    return sortedDisplayCampaigns.some(c =>
      ((c.adjustSessions || c.adjust_sessions || 0) +
        (c.adjustInstalls || c.adjust_installs || 0) +
        (c.adjustPurchaseEvents || c.adjust_purchase_events || 0) +
        (c.adjustAllRevenue || c.adjust_all_revenue || 0)) > 0
    );
  }, [sortedDisplayCampaigns]);

  // Campaign search filter
  const filteredDisplayCampaigns = useMemo(() => {
    const query = campaignSearch.trim().toLowerCase();
    if (!query) return sortedDisplayCampaigns;
    if (campaignSearchMode === 'not') {
      return sortedDisplayCampaigns.filter(c => !((c.campaignName || c.name || '').toLowerCase().includes(query)));
    }
    return sortedDisplayCampaigns.filter(c => (c.campaignName || c.name || '').toLowerCase().includes(query));
  }, [sortedDisplayCampaigns, campaignSearch, campaignSearchMode]);

  // Sadece Display endpoint'inden gelen kampanyaları göster (Search fallback engellendi)
  const campaignsToShow = filteredDisplayCampaigns;
  
  // Get summary metrics sadece Display verisinden hesaplanır (Search fallback yok)
  const rawSummaryMetrics = displayCampaignsData?.totalMetrics || calculateSummaryMetrics(displayCampaigns) || {
    impressions: 0,
    clicks: 0,
    cost: 0,
    transactions: 0,
    revenue: 0,
    ctr: 0,
    cpc: 0,
    conversionRate: 0,
    costPerTransaction: 0,
    roas: 0
  };

  // Backend'den gelen string değerleri sayıya çevir
  const summaryMetrics = {
    impressions: Number(rawSummaryMetrics.impressions) || 0,
    clicks: Number(rawSummaryMetrics.clicks) || 0,
    cost: Number(rawSummaryMetrics.cost) || 0,
    transactions: Number(rawSummaryMetrics.transactions) || 0,
    revenue: Number(rawSummaryMetrics.revenue) || 0,
    ctr: Number(rawSummaryMetrics.ctr) || 0,
    cpc: Number(rawSummaryMetrics.cpc) || 0,
    conversionRate: Number(rawSummaryMetrics.conversionRate) || 0,
    costPerTransaction: Number(rawSummaryMetrics.costPerTransaction) || 0,
    roas: Number(rawSummaryMetrics.roas) || 0,
    sessions: Number(rawSummaryMetrics.sessions) || 0,
    ga4Revenue: Number(rawSummaryMetrics.ga4Revenue || rawSummaryMetrics.ga4_revenue) || 0,
    ga4Transactions: Number(rawSummaryMetrics.ga4Transactions || rawSummaryMetrics.ga4_transactions) || 0
  };

  // Fallbacks for GA4 revenue and sessions if not provided by API
  const fallbackGa4Revenue = campaignsToShow.reduce((sum, c) => sum + (ga4ByCampaign[c.campaignId]?.ga4Revenue || 0), 0);
  const fallbackGa4TransactionsFromCampaigns = campaignsToShow.reduce((sum, c) => sum + (Number(c.ga4Transactions || c.ga4_transactions || 0)), 0);
  const fallbackGa4TransactionsFromRaw = Object.values(ga4ByCampaign).reduce((sum, v) => sum + (Number(v.ga4Transactions || 0)), 0);
  const fallbackSessions = campaignsToShow.reduce((sum, c) => sum + (Number(c.sessions) || 0), 0);
  if (!summaryMetrics.ga4Revenue && fallbackGa4Revenue) summaryMetrics.ga4Revenue = fallbackGa4Revenue;
  if (!summaryMetrics.sessions && fallbackSessions) summaryMetrics.sessions = fallbackSessions;
  if (!summaryMetrics.ga4Transactions) {
    summaryMetrics.ga4Transactions = fallbackGa4TransactionsFromCampaigns || fallbackGa4TransactionsFromRaw || 0;
  }

  const cps = summaryMetrics.sessions > 0 ? (summaryMetrics.cost || 0) / summaryMetrics.sessions : 0;

  // NOTE: Removed duplicate useEffect - fetchDisplayCampaigns is already called inside fetchKeywordData
  // This prevents double API calls when dateRange changes
  // Set default sort on mount only
  useEffect(() => {
        setSortField('budget');
        setSortDirection('desc');
  }, []);

  // Re-fetch when custom range changes (second date picked)
  useEffect(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      // NOTE: fetchKeywordData already calls fetchDisplayCampaigns internally
      fetchKeywordData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStartDate, customEndDate, dateRange]);

  // Re-fetch when route changes between /display and /demand-gen
  useEffect(() => {
    // Reset expanded states to avoid stale UI when switching tabs
    setExpandedCampaigns(new Set());
    setExpandedAdGroups(new Set());
    // NOTE: fetchKeywordData already calls fetchDisplayCampaigns internally
    // So we only need to call fetchKeywordData to avoid duplicate API calls
    fetchKeywordData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['spend', 'budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;

        return direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
      
      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status', 'text'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // Get daily data for a campaign
  const getDailyDataForCampaign = (campaignId) => {
    const dailyRecords = [];
    
    // Son X günün verilerini al (segment seçimine göre)
    const daysToShow = selectedSegment === '7days' ? 7 : 
                      selectedSegment === '14days' ? 14 : 
                      selectedSegment === '30days' ? 30 : 0;
    
    if (daysToShow === 0) return [];
    
    console.log(`🔍 Getting daily data for campaign: ${campaignId}, days: ${daysToShow}`);
    console.log('📊 Available daily data map size:', dailyDataMap.size);
    
    // 🔄 YENİ YAKLAŞIM: Seçili date range'e göre tarih aralığı hesapla
    let startDate, endDate;
    
    // Date range'e göre başlangıç ve bitiş tarihlerini hesapla
    switch(dateRange) {
      case 'yesterday':
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday;
        break;
      case 'last7':
        const last7End = new Date();
        const last7Start = new Date();
        last7Start.setDate(last7End.getDate() - 6);
        startDate = last7Start;
        endDate = last7End;
        break;
      case 'last30':
        const last30End = new Date();
        const last30Start = new Date();
        last30Start.setDate(last30End.getDate() - 29);
        startDate = last30Start;
        endDate = last30End;
        break;
      case 'thisMonth':
        const today = new Date();
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else {
          // Fallback
          const fallbackEnd = new Date();
          const fallbackStart = new Date();
          fallbackStart.setDate(fallbackEnd.getDate() - daysToShow);
          startDate = fallbackStart;
          endDate = fallbackEnd;
        }
        break;
      default:
        // Default: Son X gün
        const defaultEnd = new Date();
        const defaultStart = new Date();
        defaultStart.setDate(defaultEnd.getDate() - (daysToShow - 1));
        startDate = defaultStart;
        endDate = defaultEnd;
    }
    
    console.log(`📅 Date range: ${startDate.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
    
    // Sadece segment için istenen gün sayısını al (son X gün) - bugünü hariç tut
    const segmentEndDate = new Date(endDate);
    // Remove today: if endDate is today, step back one day
    const todayCheck = new Date();
    if (segmentEndDate.toDateString() === todayCheck.toDateString()) {
      segmentEndDate.setDate(segmentEndDate.getDate() - 1);
    }
    const segmentStartDate = new Date(segmentEndDate);
    segmentStartDate.setDate(segmentEndDate.getDate() - daysToShow + 1);
    
    console.log(`🔢 Segment range: ${segmentStartDate.toISOString().split('T')[0]} to ${segmentEndDate.toISOString().split('T')[0]}`);
    
    // Segment tarih aralığındaki günleri oluştur (lokal YYYY-MM-DD)
    const currentDate = new Date(segmentStartDate);
    while (currentDate <= segmentEndDate) {
      const dateStr = formatLocalYMD(currentDate);
      const dailyKey = `${campaignId}_${dateStr}`;
      
      console.log(`🔍 Looking for key: "${dailyKey}"`);
      
      if (dailyDataMap.has(dailyKey)) {
        // Veri var
        const data = dailyDataMap.get(dailyKey);
        console.log(`✅ Found data for ${dateStr}:`, data);
        dailyRecords.push(data);
      } else {
        // Veri yok - boş kayıt oluştur
        console.log(`❌ No data found for ${dateStr}`);
        
        // boş gün için placeholder ekle
        dailyRecords.push({
          campaignId: campaignId,
          date: dateStr,
          name: '',
          status: '',
          budget: 0,
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerConversion: 0,
          roas: 0,
          isEmpty: true // Boş kayıt olduğunu belirt
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log(`📈 Final daily records for ${campaignId}:`, dailyRecords.length, 'records');
    
    // 🚨 FALLBACK: Eğer hiç veri bulunamadıysa, o kampanyaya ait tüm günlük verileri al
    const hasAnyData = dailyRecords.some(record => !record.isEmpty);
    if (!hasAnyData) {
      console.log('🔄 No data found in date range, trying fallback approach...');
      
      // Bu kampanyaya ait tüm günlük verileri bul
      const allCampaignData = Array.from(dailyDataMap.values()).filter(data => 
        data.campaignId === campaignId
      );
      
      console.log(`📊 Found ${allCampaignData.length} total daily records for campaign ${campaignId}`);
      
      if (allCampaignData.length > 0) {
        // Son X günlük veriyi al ve döndür
        const sortedData = allCampaignData
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, daysToShow);
        
        console.log('📈 Using fallback data:', sortedData);
        return sortedData;
      }
    }
    
    return dailyRecords.sort((a, b) => new Date(b.date) - new Date(a.date)); // En son tarih üstte
  };

  // Click-outside handler ekleyin
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
      if (isSegmentDropdownOpen) {
        setIsSegmentDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDateDropdownOpen, isSegmentDropdownOpen]);

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70 w-full max-w-[100vw] overflow-x-hidden box-border">
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' : 'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDialog(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200">Cancel</button>
              <button onClick={confirmDialog.onConfirm} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
              }`}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white px-5 py-3 rounded-2xl shadow-sm w-full gap-3 md:gap-0 min-w-0">
        <div className="flex items-center gap-2.5 w-full md:w-auto">
          {/* Icon changes by page */}
          <img
            src={isDemandGen ? "/demandgen.svg" : "/display.svg"}
            alt={isDemandGen ? "Demand Gen" : "Display Campaigns"}
            className="w-7 h-7 object-contain"
          />
          <h1 className="text-lg font-semibold text-gray-900">{isDemandGen ? 'Demand Gen' : 'Display Campaigns'}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-3 justify-center md:justify-end w-full md:w-auto">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı */}
          <div className="text-xs text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'lastMonth':
                  const lastMonthFirstDay = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
                  const lastMonthLastDay = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
                  return `${lastMonthFirstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${lastMonthLastDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces legacy dropdown + modal) */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />

        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-w-0">
        {/* Chart */}
        <Card className="col-span-1 lg:col-span-8 rounded-2xl overflow-hidden bg-white min-w-0">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Daily Cost & Revenue Trend</h2>
                <p className="text-sm text-gray-500 mt-1">{isDemandGen ? 'Demand Gen campaign daily cost and revenue distribution' : 'Display campaign daily cost and revenue distribution'}</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {dataLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics - kartların sırasını değiştirdik */}
        <div className="col-span-1 lg:col-span-4 grid grid-cols-1 gap-3 min-w-0">
          {/* Row 1: Revenue, GA4 Revenue */}
          <div className="grid grid-cols-2 gap-3">
            {/* Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Revenue</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  ₺{(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.roas || 0)}x ROAS</p>
              </div>
            </div>
            {/* GA4 Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">GA4 Revenue</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  ₺{(!isNaN(summaryMetrics.ga4Revenue) && summaryMetrics.ga4Revenue ? Math.floor(summaryMetrics.ga4Revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.ga4Roas || 0)}x ROAS</p>
              </div>
            </div>
          </div>

          {/* Row 2: Cost, Conversions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cost Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Cost</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">
                  ₺{(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Conversions Card (Combined) */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Conversions</p>
                <div className="flex items-center gap-1">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-3 h-3" />
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                </div>
              </div>
              <div className="flex items-center justify-around mt-2">
                {/* Ads Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.conversions || summaryMetrics.transactions || 0}</p>
                  <p className="text-[10px] text-gray-500">%{formatNumber(summaryMetrics.conversionRate ? summaryMetrics.conversionRate * 100 : 0, {maximumFractionDigits: 0})} CR</p>
                </div>
                {/* Separator */}
                <div className="h-10 border-l border-gray-200"></div>
                {/* GA4 Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.ga4Transactions || 0}</p>
                  <p className="text-[10px] text-gray-500">GA4</p>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Clicks, Sessions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Clicks Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Clicks</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Sessions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Sessions</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>
          
          {/* Row 4: CPC, CPS */}
          <div className="grid grid-cols-2 gap-3">
            {/* CPC Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPC</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{formatNumber(summaryMetrics.cpc || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* CPS Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPS</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{formatNumber(cps || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white w-full min-w-0">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0 min-w-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{isDemandGen ? 'Demand Gen Performance' : 'Display Campaign Performance'}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedSegment !== 'none'
                  ? `Daily breakdown for campaigns (${selectedSegment === '7days' ? '7' : selectedSegment === '14days' ? '14' : '30'} days)`
                  : 'Detailed metrics for display campaigns and ad groups'
                }
              </p>
            </div>

            {/* Search + Segments */}
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              {/* Search Bar */}
              <div className="relative flex-grow md:flex-grow-0">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  placeholder="Search campaigns"
                  className="pl-9 pr-8 py-2.5 w-full md:w-64 bg-white border rounded-xl text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
                {campaignSearch && (
                  <button
                    onClick={() => setCampaignSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Contains / Not contains selector */}
              <div>
                <select
                  value={campaignSearchMode}
                  onChange={(e) => setCampaignSearchMode(e.target.value)}
                  className="px-2.5 py-2.5 bg-white border rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  <option value="contains">contains</option>
                  <option value="not">not contains</option>
                </select>
              </div>

              {/* Segments Dropdown */}
              <div className="relative inline-block">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSegmentDropdownOpen(!isSegmentDropdownOpen);
                    // When opening, scroll the upcoming daily table into view
                    setTimeout(() => {
                      if (dailyTableRef.current) {
                        dailyTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 150);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LayoutList className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">
                    {selectedSegment === 'none' && 'Segments'}
                    {selectedSegment === '7days' && '7 Days'}
                    {selectedSegment === '14days' && '14 Days'}
                    {selectedSegment === '30days' && '30 Days'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {/* Segments dropdown menu */}
                {isSegmentDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-3 pb-2 mb-2 border-b border-gray-100">
                      <h3 className="text-xs font-medium text-gray-500 uppercase">Date Segments</h3>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedSegment('none');
                        setIsSegmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        selectedSegment === 'none'
                          ? 'text-blue-600 bg-blue-50/50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      None
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSegment('7days');
                        setIsSegmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        selectedSegment === '7days'
                          ? 'text-blue-600 bg-blue-50/50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Last 7 Days
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSegment('14days');
                        setIsSegmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        selectedSegment === '14days'
                          ? 'text-blue-600 bg-blue-50/50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Last 14 Days
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSegment('30days');
                        setIsSegmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        selectedSegment === '30days'
                          ? 'text-blue-600 bg-blue-50/50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Last 30 Days
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto w-full max-w-full block">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : displayCampaigns.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No display campaign data available</p>
                <p className="text-sm text-gray-500">Connect your Google Ads account to see display campaign performance</p>
              </div>
            </div>
          ) : campaignsToShow.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No matching campaigns</p>
                <p className="text-sm text-gray-500">Try a different search query or date range</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse min-w-max">
              <thead>
                <tr>
                  <th
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('text')}
                  >
                    Campaign / Ad Group
                    {sortField === 'text' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleSort('budget')}>
                    Budget
                    {sortField === 'budget' && (<span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>)}
                  </th>
                  <th
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('spend')}
                  >
                    Cost
                    {sortField === 'spend' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impr.
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ctr')}
                  >
                    CTR
                    {sortField === 'ctr' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpc')}
                  >
                    Avg. CPC
                    {sortField === 'cpc' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conv.
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                    {sortField === 'conversionRate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionValue')}
                  >
                    Revenue
                    {sortField === 'conversionValue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200">
                    GA4 Rev.
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ga4Transactions')}
                  >
                    GA4 Trans.
                    {sortField === 'ga4Transactions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  {hasAdjust && (
                    <>
                  {hasAdjust && (
                    <>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustSessions')}
                      >
                        Adj. Sessions
                        {sortField === 'adjustSessions' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustInstalls')}
                      >
                        Adj. Installs
                        {sortField === 'adjustInstalls' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustPurchaseEvents')}
                      >
                        Adj. Purchases
                        {sortField === 'adjustPurchaseEvents' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustAllRevenue')}
                      >
                        Adj. Revenue
                        {sortField === 'adjustAllRevenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-700 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50 bg-emerald-50/40"
                        onClick={() => handleSort('totalCombinedRevenue')}
                        title="GA4 Revenue + Adjust Revenue"
                      >
                        <div className="flex items-center justify-end gap-1">
                          <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3 inline-block" />
                          <span className="text-gray-400">+</span>
                          <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3 inline-block" />
                          <span>Total Rev.</span>
                        </div>
                        {sortField === 'totalCombinedRevenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    </>
                  )}
                    </>
                  )}
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    Sessions
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('roas')}
                  >
                    ROAS
                    {sortField === 'roas' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaignsToShow.map((campaign) => (
                  <React.Fragment key={campaign.campaignId || campaign.id}>
                    {/* Campaign Row */}
                    <tr
                      className={`group/row hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaign.campaignId || campaign.id) ? 'bg-blue-50/50 font-semibold' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaign.campaignId || campaign.id, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200 relative">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-5 h-5">
                            {((campaign.adGroups && campaign.adGroups.length > 0) || selectedSegment !== 'none') ? (
                              expandedCampaigns.has(campaign.campaignId || campaign.id) ?
                              <ChevronDown className="w-4 h-4 text-blue-500" /> :
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            ) : (
                              <span className="w-4 h-4"></span>
                            )}
                          </div>
                          {/* Status dot + hover arrow + Campaign Name */}
                          {(() => {
                            const cid = campaign.campaignId || campaign.id;
                            const live = liveDataMap[String(cid)];
                            const resolvedStatus = cleanStatus(live?.status || campaign.status || '');
                            const liveId = live?.id || cid;
                            return (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenStatusDropdown(openStatusDropdown === cid ? null : cid); }}
                                  disabled={updatingId === cid}
                                  className="flex-shrink-0 -ml-1 flex items-center gap-0.5 px-1 py-1 rounded-md hover:bg-gray-100 transition-colors focus:outline-none group/dot"
                                  title={resolvedStatus === 'ENABLED' ? 'Enabled — click to change' : 'Paused — click to change'}
                                >
                                  {updatingId === cid ? (
                                    <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                  ) : (
                                    <div className={`w-2.5 h-2.5 rounded-full ${resolvedStatus === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' : 'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'}`} />
                                  )}
                                  <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                </button>
                                {openStatusDropdown === cid && (
                                  <>
                                    <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                    <div className="absolute top-full left-8 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Enable Campaign',
                                            message: `Are you sure you want to enable campaign "${campaign.campaignName || campaign.name}"?`,
                                            variant: 'enable',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'ENABLED', cid, resolvedStatus, campaign.campaignName || campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-green-500" />
                                        <span>Enable</span>
                                        {resolvedStatus === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenStatusDropdown(null);
                                          setConfirmDialog({
                                            title: 'Pause Campaign',
                                            message: `Are you sure you want to pause campaign "${campaign.campaignName || campaign.name}"?`,
                                            variant: 'pause',
                                            onConfirm: () => {
                                              updateEntity('campaign', liveId, 'status', 'PAUSED', cid, resolvedStatus, campaign.campaignName || campaign.name);
                                              setConfirmDialog(null);
                                            }
                                          });
                                        }}
                                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                          ${resolvedStatus === 'PAUSED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                        `}
                                      >
                                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                                        <span>Pause</span>
                                        {resolvedStatus === 'PAUSED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                          <img src={isDemandGen ? "/demandgen.svg" : "/display.svg"} alt="Display Campaign" className="w-4 h-4" />
                          <p className="text-[13px] text-gray-900">{campaign.campaignName || campaign.name || 'Unknown Campaign'}</p>
                        </div>
                      </td>
                      {/* Budget inline edit cell */}
                      <td className="px-3 py-2.5 whitespace-nowrap text-right border-r border-gray-200" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const cid = campaign.campaignId || campaign.id;
                          const live = liveDataMap[String(cid)];
                          const isEditing = editBudget?.id === cid;
                          const displayBudget = live?.budget != null ? live.budget : (campaign.budget ?? null);
                          const hasBudgetId = !!(live?.budgetId);

                          if (isEditing) {
                            return (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  value={editBudget.value}
                                  onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveBudget(cid, campaign.campaignName || campaign.name);
                                    if (e.key === 'Escape') setEditBudget(null);
                                  }}
                                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                  autoFocus
                                />
                                <button onClick={() => handleSaveBudget(cid, campaign.campaignName || campaign.name)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditBudget(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-end gap-2 group">
                              <span className="text-sm text-gray-900 font-medium">
                                {displayBudget != null ? `₺${displayBudget.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : isLoadingLive ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 inline" /> : '—'}
                              </span>
                              {hasBudgetId && (
                                <button
                                  onClick={() => setEditBudget({ id: cid, value: displayBudget || 0 })}
                                  disabled={updatingId === cid}
                                  className="opacity-0 group-hover/row:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* Cost cell */}
                      <td className="text-right py-2.5 px-3 border-r border-gray-200">
                        <div className="text-[13px] font-medium text-gray-900">₺{formatNumber((campaign.cost && campaign.cost > 0) ? campaign.cost : (campaign.spend || 0))}</div>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.impressions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.ctr) ? '0' : formatNumber(campaign.ctr, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.cpc) ? '0' : formatNumber(campaign.cpc, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{campaign.conversions ?? campaign.transactions ?? campaign.google_ads_transaction ?? 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.conversionRate) ? '0' : formatNumber(campaign.conversionRate, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{formatNumber((campaign.conversionValue || campaign.revenue || 0))}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(ga4ByCampaign[campaign.campaignId]?.ga4Revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                      {/* GA4 Transactions column */}
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.ga4Transactions || campaign.ga4_transactions || ga4ByCampaign[campaign.campaignId]?.ga4Transactions || 0).toLocaleString()}</td>
                      {hasAdjust && (
                        <>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustSessions || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustInstalls || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustPurchaseEvents || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(campaign.adjustAllRevenue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-semibold text-emerald-700 bg-emerald-50/30">₺{(((ga4ByCampaign[campaign.campaignId]?.ga4Revenue) || 0) + (campaign.adjustAllRevenue || 0)).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        </>
                      )}
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.sessions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{isNaN(campaign.roas) ? '0' : formatNumber(campaign.roas, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x</td>
                    </tr>

                    {/* Daily Segments Section */}
                    {expandedCampaigns.has(campaign.campaignId || campaign.id) && selectedSegment !== 'none' && (
                      <>
                        {/* Scroll target for daily table */}
                        <tr ref={dailyTableRef}></tr>
                        {getDailyDataForCampaign(campaign.campaignId || campaign.id).map((dailyData, dayIndex) => (
                          <tr 
                            key={`${campaign.campaignId || campaign.id}_${dailyData.date}`}
                            className={`hover:bg-blue-50/10 ${dailyData.isEmpty ? 'bg-red-50/30 border-l-2 border-red-300' : 'bg-blue-50/5'}`}
                          >
                            <td className="py-2 px-3 border-r border-gray-200">
                              <div className="flex items-center gap-2 pl-[2.75rem]">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-gray-400" />
                                  <div>
                                    <p className={`text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                      {new Date(dailyData.date).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short'
                                      })}
                                    </p>
                                    {dailyData.isEmpty && (
                                      <p className="text-[11px] text-red-400">No data</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {/* Budget — empty for daily sub-rows */}
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${formatNumber(dailyData.spend)}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.impressions || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.clicks || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${formatNumber(Number(dailyData.ctr || 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${formatNumber(Number(dailyData.cpc || 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.conversions || 0)}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${formatNumber(Number(dailyData.conversionRate || 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${(dailyData.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}
                            </td>
                            {/* GA4 Daily: Revenue, Transactions, Sessions */}
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `₺${(dailyData.ga4Revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.ga4Transactions || 0)}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : (dailyData.sessions || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 text-[13px] font-medium ${dailyData.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {dailyData.isEmpty ? '—' : `${formatNumber(Number(dailyData.roas || 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Ad Groups Section */}
                    {expandedCampaigns.has(campaign.campaignId || campaign.id) && selectedSegment === 'none' && campaign.adGroups && (
                      <>
                        {sortAdGroups(campaign.adGroups, sortField, sortDirection).map((adGroup) => (
                          <React.Fragment key={adGroup.id}>
                            {/* Ad Group Row */}
                            <tr 
                              className="hover:bg-blue-50/20 bg-blue-50/5"
                              onClick={(e) => toggleAdGroup(adGroup.id, e)}
                            >
                              <td className="py-2 px-3 border-r border-gray-200">
                                <div className="flex items-center gap-2 pl-[2.75rem]">
                                  <div className="flex items-center justify-center w-5 h-5">
                                    {expandedAdGroups.has(adGroup.id) ? 
                                      <ChevronDown className="w-4 h-4 text-blue-500" /> :
                                      <ChevronRight className="w-4 h-4 text-gray-400" />
                                    }
                                  </div>
                                  {/* LayoutList ikonu yerine enableicon.svg kullanıyoruz - küçük boyutta */}
                                  <div className="w-5 h-5 flex items-center justify-center">
                                    <img 
                                      src="/enableicon.svg" 
                                      alt="Ad Group" 
                                      className="w-3 h-3" 
                                    />
                                  </div>
                                  <div>
                                    <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                                  </div>
                                </div>
                              </td>
                              {/* Budget — empty for ad group sub-rows */}
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.spend)}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.ctr) ? '0' : formatNumber(adGroup.ctr, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.cpc) ? '0' : formatNumber(adGroup.cpc, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{adGroup.conversions || 0}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.conversionRate) ? '0' : formatNumber(adGroup.conversionRate, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.costPerConversion) ? '0' : formatNumber(adGroup.costPerConversion, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.conversionValue || 0)}</td>
                              <td className="text-right py-2 px-3 text-[13px] font-medium text-gray-800">{isNaN(adGroup.roas) ? '0' : formatNumber(adGroup.roas, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x</td>
                            </tr>

                            {/* Keywords Section */}
                            {expandedAdGroups.has(adGroup.id) && adGroup.keywords && sortKeywords(adGroup.keywords, sortField, sortDirection).map((keyword) => (
                              <tr 
                                key={keyword.id}
                                className="hover:bg-blue-50/10 bg-blue-50/2"
                              >
                                <td className="py-2 px-3 border-r border-gray-200">
                                  <div className="flex items-center gap-2 pl-[5.5rem]">
                                    {/* 'keyword.text' yerine 'keyword.text || "Placement/Target"' kullanarak hem keyword hem placement verilerini gösterebiliriz */}
                                    <p className="text-[12px] font-medium text-gray-700">{keyword.text}</p>
                                    <div className="flex gap-1">
                                      {/* Eşleşme tipi yerine yerleşim türünü (website, video, app vb.) gösterebiliriz */}
                                      {keyword.matchTypes && keyword.matchTypes.map((matchType, index) => (
                                        <span key={index} className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                          matchType.includes('EXACT') ? 'bg-blue-100 text-blue-700' :
                                          matchType.includes('PHRASE') ? 'bg-green-100 text-green-700' :
                                          matchType.includes('BROAD') ? 'bg-purple-100 text-purple-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {/* Match type değiştirildi */}
                                          {matchType.replace('KeywordMatchType.', '').replace('KEYWORD_MATCH_TYPE_', '')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                                {/* Budget — empty for keyword sub-rows */}
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-400">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">₺{formatNumber(keyword.spend)}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.impressions || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{(keyword.clicks || 0).toLocaleString()}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.impressions > 0 ? formatNumber((keyword.clicks / keyword.impressions) * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.clicks > 0 ? (keyword.spend / keyword.clicks) : 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">{keyword.conversions || 0}</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  {keyword.clicks > 0 ? formatNumber((keyword.conversions / keyword.clicks) * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0'}%
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{formatNumber(keyword.conversions > 0 ? (keyword.spend / keyword.conversions) : 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[12px] font-medium text-gray-700">
                                  ₺{(keyword.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                <td className="text-right py-2 px-3 text-[12px] font-medium text-gray-700">
                                  {formatNumber(keyword.spend > 0 ? (keyword.conversionValue / keyword.spend) : 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Toplam Satırı - SADECE display kampanyaları varsa göster */}
              {campaignsToShow.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                      Total
                    </td>
                    {/* Budget column — no total */}
                    <td className="text-right py-3 px-3 border-r border-gray-200"></td>
                    {(() => {
                      const totalSpend = campaignsToShow.reduce((t, c) => t + (Number(c.cost) || Number(c.spend) || 0), 0);
                      const totalImpr = campaignsToShow.reduce((t, c) => t + (Number(c.impressions) || 0), 0);
                      const totalClicks = campaignsToShow.reduce((t, c) => t + (Number(c.clicks) || 0), 0);
                      const totalConv = campaignsToShow.reduce((t, c) => t + (Number(c.conversions) || Number(c.transactions) || Number(c.google_ads_transaction) || 0), 0);
                      const totalRevenue = campaignsToShow.reduce((t, c) => t + (Number(c.conversionValue) || Number(c.revenue) || 0), 0);
                      const totalSessions = campaignsToShow.reduce((t, c) => t + (Number(c.sessions) || 0), 0);
                      const totalGa4Revenue = campaignsToShow.reduce((sum, c) => sum + (Number(ga4ByCampaign[c.campaignId]?.ga4Revenue) || 0), 0);
                      const totalGa4Trans = campaignsToShow.reduce((sum, c) => sum + (Number(c.ga4Transactions || c.ga4_transactions || ga4ByCampaign[c.campaignId]?.ga4Transactions || 0)), 0);
                      const totalAdjSessions = campaignsToShow.reduce((t, c) => t + (Number(c.adjustSessions) || 0), 0);
                      const totalAdjInstalls = campaignsToShow.reduce((t, c) => t + (Number(c.adjustInstalls) || 0), 0);
                      const totalAdjPurchases = campaignsToShow.reduce((t, c) => t + (Number(c.adjustPurchaseEvents) || 0), 0);
                      const totalAdjRevenue = campaignsToShow.reduce((t, c) => t + (Number(c.adjustAllRevenue) || 0), 0);
                      const ctr = totalImpr > 0 ? formatNumber((totalClicks / totalImpr) * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
                      const avgCpc = totalClicks > 0 ? formatNumber(totalSpend / totalClicks, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
                      const convRate = totalClicks > 0 ? formatNumber((totalConv / totalClicks) * 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
                      const roas = totalSpend > 0 ? formatNumber(totalRevenue / totalSpend, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0';
                      return (
                        <>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(totalSpend)}</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalImpr.toLocaleString()}</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalClicks.toLocaleString()}</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{ctr}%</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{avgCpc}</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalConv.toLocaleString()}</td>
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{convRate}%</td>
                          {/* Revenue total */}
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(totalRevenue)}</td>
                          {/* GA4 Revenue total */}
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(totalGa4Revenue)}</td>
                          {/* GA4 Transactions total */}
                          <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalGa4Trans.toLocaleString()}</td>
                          {hasAdjust && (
                            <>
                              {/* Adjust totals */}
                              <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalAdjSessions.toLocaleString()}</td>
                              <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalAdjInstalls.toLocaleString()}</td>
                              <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">{totalAdjPurchases.toLocaleString()}</td>
                              <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">₺{formatNumber(totalAdjRevenue)}</td>
                              <td className="text-right py-3 px-3 border-r border-gray-200 font-semibold text-[13px] text-emerald-700 bg-emerald-50/30">₺{formatNumber(totalGa4Revenue + totalAdjRevenue)}</td>
                            </>
                          )}
                          {/* Sessions total */}
                          <td className="text-right py-3 px-3 border-l border-gray-200 font-medium text-[13px] text-gray-900">{totalSessions.toLocaleString()}</td>
                          <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">{roas}x</td>
                        </>
                      );
                    })()}
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </Card>
      
      {/* Top Keywords Card'ı, Top Placements Card'ı olarak değiştirildi */}
      {!isDemandGen && (
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GDN Placements - primary field: placement URL */}
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  {!isDemandGen && (
                    <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Placement URL</th>
                  )}
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Impr.</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Clicks</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Cost</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPC</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(filteredGdnPlacements || []).slice(0, 100).map((p, idx) => {
                  const rawUrl = (p.domain || '').toString();
                  const href = p.href || (rawUrl ? `https://${rawUrl}` : '#');
                  const impressions = Number(p.impressions || 0);
                  const clicks = Number(p.clicks || 0);
                  const cost = Number(p.cost || 0);
                  const cpc = clicks > 0 ? (cost / clicks) : 0;
                  const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0;
                  return (
                    <React.Fragment key={p.domain || idx}>
                      <tr className="hover:bg-blue-50/20">
                        {!isDemandGen && (
                          <td className="py-2 px-3 text-[13px] text-gray-800 break-all">
                            <button
                              onClick={(e) => { e.preventDefault(); togglePlacementDomain(p.domain); }}
                              className="inline-flex items-center justify-center mr-2 text-gray-600 hover:text-gray-900"
                              aria-label="Toggle breakdown"
                            >
                              {expandedDomains.has(p.domain) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            {rawUrl ? (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                                {rawUrl}
                              </a>
                            ) : '—'}
                          </td>
                        )}
                        <td className="py-2 px-3 text-right text-[13px] text-gray-800">{impressions.toLocaleString('tr-TR')}</td>
                        <td className="py-2 px-3 text-right text-[13px] text-gray-800">{clicks.toLocaleString('tr-TR')}</td>
                        <td className="py-2 px-3 text-right text-[13px] text-gray-800">₺{cost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-2 px-3 text-right text-[13px] text-gray-800">₺{cpc.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-2 px-3 text-right text-[13px] text-gray-800">₺{cpm.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                      {expandedDomains.has(p.domain) && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={isDemandGen ? 5 : 6} className="py-2 px-3">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    <th className="text-left py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">Campaign</th>
                                    <th className="text-right py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">Impr.</th>
                                    <th className="text-right py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">Clicks</th>
                                    <th className="text-right py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">Cost</th>
                                    <th className="text-right py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">CPC</th>
                                    <th className="text-right py-1 px-2 text-[11px] font-medium text-gray-600 uppercase tracking-wider">CPM</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getCampaignBreakdownForDomain(p.domain).map((c) => {
                                    const imp = Number(c.impressions || 0);
                                    const clk = Number(c.clicks || 0);
                                    const cst = Number(c.cost || 0);
                                    const cpcB = clk > 0 ? (cst / clk) : 0;
                                    const cpmB = imp > 0 ? (cst / imp) * 1000 : 0;
                                    return (
                                      <tr key={`${p.domain}-${c.campaignName}`}>
                                        <td className="py-1 px-2 text-[13px] text-gray-800">{c.campaignName}</td>
                                        <td className="py-1 px-2 text-right text-[13px] text-gray-800">{imp.toLocaleString('tr-TR')}</td>
                                        <td className="py-1 px-2 text-right text-[13px] text-gray-800">{clk.toLocaleString('tr-TR')}</td>
                                        <td className="py-1 px-2 text-right text-[13px] text-gray-800">₺{cst.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td className="py-1 px-2 text-right text-[13px] text-gray-800">₺{cpcB.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        <td className="py-1 px-2 text-right text-[13px] text-gray-800">₺{cpmB.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {((filteredGdnPlacements || []).length === 0) && (
                  <tr>
                    <td className="py-4 px-3 text-center text-[13px] text-gray-500" colSpan={6}>No data</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-white">
                {(() => {
                  const totalsSource = gdnPlacementTotals || computeGdnTotals((filteredGdnPlacements || []).slice(0,100));
                  const label = gdnPlacementTotals ? 'Total (All filtered)' : 'Total (Top 100)';
                  return (
                    <tr className="bg-gray-50">
                      <td className="py-2 px-3 text-[12px] font-semibold text-gray-700">{label}</td>
                      <td className="py-2 px-3 text-right text-[12px] font-semibold text-gray-700">
                        {Number(totalsSource.impressions || 0).toLocaleString('tr-TR')}
                      </td>
                      <td className="py-2 px-3 text-right text-[12px] font-semibold text-gray-700">
                        {Number(totalsSource.clicks || 0).toLocaleString('tr-TR')}
                      </td>
                      <td className="py-2 px-3 text-right text-[12px] font-semibold text-gray-700">
                        ₺{Number(totalsSource.cost || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right text-[12px] font-semibold text-gray-700">
                        ₺{Number(totalsSource.clicks > 0 ? totalsSource.cost / totalsSource.clicks : totalsSource.cpc || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right text-[12px] font-semibold text-gray-700">
                        ₺{Number(totalsSource.impressions > 0 ? (totalsSource.cost / totalsSource.impressions) * 1000 : totalsSource.cpm || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Placement Type Performance */}
        <Card className="rounded-2xl overflow-hidden bg-white">
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  {!isDemandGen && (
                    <th className="text-left py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Placement Type</th>
                  )}
                  <th className="text-right py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Impr.</th>
                  <th className="text-right py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Clicks</th>
                  <th className="text-right py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">Cost</th>
                  <th className="text-right py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">CPC</th>
                  <th className="text-right py-2 px-3 text-[14px] font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">CPM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {placementTypeRows.map((t, idx) => {
                  const impressions = Number(t.impressions || 0);
                  const clicks = Number(t.clicks || 0);
                  const cost = Number(t.cost || 0);
                  const cpc = clicks > 0 ? (cost / clicks) : 0;
                  const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0;
                  return (
                    <tr key={t.type || idx} className="hover:bg-blue-50/20">
                      {!isDemandGen && (
                        <td className="py-3 px-3 text-[14px] text-gray-900">{(t.type || 'UNKNOWN').replace(/^PlacementType\./, '')}</td>
                      )}
                      <td className="py-3 px-3 text-right text-[14px] text-gray-900">{impressions.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right text-[14px] text-gray-900">{clicks.toLocaleString('tr-TR')}</td>
                      <td className="py-3 px-3 text-right text-[14px] text-gray-900">₺{cost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-3 text-right text-[14px] text-gray-900">₺{cpc.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-3 text-right text-[14px] text-gray-900">₺{cpm.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
                {placementTypeRows.length === 0 && (
                  <tr>
                    <td className="py-3 px-3 text-[14px] text-gray-500" colSpan={isDemandGen ? 5 : 6}>No data</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-white">
                <tr className="bg-gray-50">
                  {!isDemandGen && (
                    <td className="py-2 px-3 text-[13px] font-semibold text-gray-700">Total</td>
                  )}
                  <td className="py-2 px-3 text-right text-[13px] font-semibold text-gray-700">{computeGdnTotals(placementTypeRows).impressions.toLocaleString('tr-TR')}</td>
                  <td className="py-2 px-3 text-right text-[13px] font-semibold text-gray-700">{computeGdnTotals(placementTypeRows).clicks.toLocaleString('tr-TR')}</td>
                  <td className="py-2 px-3 text-right text-[13px] font-semibold text-gray-700">₺{computeGdnTotals(placementTypeRows).cost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right text-[13px] font-semibold text-gray-700">₺{computeGdnTotals(placementTypeRows).cpc.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right text-[13px] font-semibold text-gray-700">₺{computeGdnTotals(placementTypeRows).cpm.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>
      )}

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}

      {/* Change History Slide-over */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();
                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };
                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' : isStatus ? 'bg-amber-100' : isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' : isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}